import { useCallback, useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, Paperclip, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { financeService, type FinanceRequestDetail } from '@/services/financeService'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import { cn } from '@/utils/cn'
import { FINANCE_DETAIL_FIELD_CLASS } from '@/components/Financeiro/FinanceDetailSectionCard'

type PendingFile = {
  id: string
  file: File
  previewUrl?: string
}

type Props = {
  detail: FinanceRequestDetail
  canManageQueue: boolean
  onAddComment: (
    body: string,
    isInternal?: boolean,
    attachments?: string[],
  ) => Promise<boolean>
  compact?: boolean
}

function isImageAttachment(url: string): boolean {
  return /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url)
}

function attachmentLabel(url: string): string {
  const segment = url.split('/').pop() ?? 'arquivo'
  return segment.replace(/^\d+-[a-f0-9-]+/i, '') || segment
}

export function FinanceRequestCommentsPanel({
  detail,
  canManageQueue,
  onAddComment,
  compact,
}: Props) {
  const [comment, setComment] = useState('')
  const [internalComment, setInternalComment] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    return () => {
      pendingFiles.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      })
    }
  }, [pendingFiles])

  const addPendingFiles = useCallback((files: FileList | File[]) => {
    const next = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    }))
    setPendingFiles((prev) => [...prev, ...next].slice(0, 10))
  }, [])

  const removePendingFile = (id: string) => {
    setPendingFiles((prev) => {
      const target = prev.find((item) => item.id === id)
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((item) => item.id !== id)
    })
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = event.clipboardData?.items
    if (!items) return

    const imageFiles: File[] = []
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) imageFiles.push(file)
      }
    }

    if (imageFiles.length > 0) {
      event.preventDefault()
      addPendingFiles(imageFiles)
    }
  }

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!comment.trim() && pendingFiles.length === 0) return

    setSubmitting(true)
    try {
      const attachmentUrls: string[] = []
      for (const item of pendingFiles) {
        const uploaded = await financeService.uploadRequestAttachment(item.file)
        attachmentUrls.push(uploaded.fileUrl)
      }

      const ok = await onAddComment(
        comment.trim(),
        canManageQueue ? internalComment : false,
        attachmentUrls,
      )

      if (ok) {
        setComment('')
        setInternalComment(false)
        pendingFiles.forEach((item) => {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
        })
        setPendingFiles([])
      }
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit = Boolean(comment.trim() || pendingFiles.length > 0)

  return (
    <div className='space-y-4'>
      <ul className={cn('space-y-2', compact ? 'max-h-64 overflow-y-auto pr-1' : 'space-y-3')}>
        {(detail.comments ?? []).length === 0 ? (
          <li className='rounded-lg border border-dashed border-white/[0.08] px-3 py-6 text-center text-sm text-text-muted'>
            Nenhum comentário ainda.
          </li>
        ) : (
          (detail.comments ?? []).map((c) => (
            <li
              key={c.id}
              className={cn(
                'rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 text-sm',
                c.isInternal && 'border-amber-500/30 bg-amber-500/5',
              )}
            >
              <div className='flex flex-wrap items-center gap-2'>
                <p className='font-semibold text-text-primary'>{c.author.name}</p>
                {c.isInternal && (
                  <span className='rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300'>
                    Interno
                  </span>
                )}
                <span className='text-xs text-text-muted'>
                  {new Date(c.createdAt).toLocaleString('pt-BR')}
                </span>
              </div>
              {c.body ? (
                <p className='mt-1.5 whitespace-pre-wrap text-text-primary'>{c.body}</p>
              ) : null}
              {c.attachments && c.attachments.length > 0 && (
                <div className='mt-2 flex flex-wrap gap-2'>
                  {c.attachments.map((url) => {
                    const href = resolvePublicAssetUrl(url)
                    if (isImageAttachment(url)) {
                      return (
                        <a
                          key={url}
                          href={href}
                          target='_blank'
                          rel='noreferrer'
                          className='block overflow-hidden rounded-lg border border-white/[0.08]'
                        >
                          <img
                            src={href}
                            alt='Anexo'
                            className='max-h-40 max-w-[220px] object-cover'
                          />
                        </a>
                      )
                    }
                    return (
                      <a
                        key={url}
                        href={href}
                        target='_blank'
                        rel='noreferrer'
                        className='inline-flex items-center gap-1 rounded-md border border-white/[0.08] bg-white/[0.03] px-2 py-1 text-xs text-accent hover:underline'
                      >
                        <Paperclip size={12} />
                        {attachmentLabel(url)}
                      </a>
                    )
                  })}
                </div>
              )}
            </li>
          ))
        )}
      </ul>

      <form onSubmit={handleComment} className='space-y-2 border-t border-white/[0.06] pt-4'>
        {canManageQueue && (
          <label className='flex items-center gap-2 text-xs text-text-muted'>
            <input
              type='checkbox'
              checked={internalComment}
              onChange={(e) => setInternalComment(e.target.checked)}
              className='rounded border-border'
            />
            Comentário interno
          </label>
        )}

        <textarea
          className={cn(FINANCE_DETAIL_FIELD_CLASS, 'min-h-[88px] resize-y')}
          rows={3}
          placeholder='Escreva uma mensagem para o time… (cole prints com Ctrl+V)'
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onPaste={handlePaste}
        />

        {pendingFiles.length > 0 && (
          <div className='flex flex-wrap gap-2'>
            {pendingFiles.map((item) => (
              <div
                key={item.id}
                className='relative overflow-hidden rounded-lg border border-white/[0.08] bg-white/[0.03]'
              >
                {item.previewUrl ? (
                  <img
                    src={item.previewUrl}
                    alt={item.file.name}
                    className='h-20 w-20 object-cover'
                  />
                ) : (
                  <div className='flex h-20 w-28 items-center gap-1 px-2 text-xs text-text-muted'>
                    <Paperclip size={12} />
                    <span className='line-clamp-2'>{item.file.name}</span>
                  </div>
                )}
                <button
                  type='button'
                  onClick={() => removePendingFile(item.id)}
                  className='absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white'
                  aria-label='Remover anexo'
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className='flex flex-wrap items-center gap-2'>
          <input
            ref={fileInputRef}
            type='file'
            multiple
            accept='image/*,.pdf,.doc,.docx,.xls,.xlsx'
            className='hidden'
            onChange={(e) => {
              if (e.target.files?.length) addPendingFiles(e.target.files)
              e.target.value = ''
            }}
          />
          <Button
            type='button'
            size='sm'
            variant='secondary'
            icon={<Paperclip size={14} />}
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting}
          >
            Anexar
          </Button>
          <Button
            type='button'
            size='sm'
            variant='secondary'
            icon={<ImagePlus size={14} />}
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting}
          >
            Imagem
          </Button>
          <Button type='submit' size='sm' disabled={submitting || !canSubmit} className='ml-auto'>
            {submitting ? (
              <>
                <Loader2 size={14} className='animate-spin' />
                Enviando…
              </>
            ) : (
              'Enviar mensagem'
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
